import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from 'src/components/iconify';
import { RootState } from 'src/context/store';
import ExploreChart, { getMetricEvolutionColor } from '../explore/ExploreChart';
import {
    endOfMonth,
    endOfWeek,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from 'date-fns';
import {
    setAnalysisChartCompareDate,
    setAnalysisChartCompareMode,
    setAnalysisChartDate,
    setAnalysisChartTimestep,
} from 'src/context/reducers/filter-settings/filterSlice';
import { useKPIToggle } from '../explore/ExploreKPIChart';
import { useGetConversionsKpisQuery } from 'src/context/api/conversions/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { brandedCampaignsVisibility } from 'src/utils/branded-campaigns';
import ExploreChartExportModal, {
    EXPORT_FORMATS,
    type ExportFormat,
    type ExportOptions,
} from './ExploreChartExportModal';
type ConversionsAnalysisChartModalProps = {
    open: boolean;
    onClose: () => void;
};
/**
 * Convertit une date locale en ISO à midi UTC.
 *
 * Utiliser midi évite que le changement de fuseau transforme
 * un lundi local en dimanche UTC.
 */
const toUtcNoonISOString = (date: Date): string =>
    new Date(
        Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            12,
            0,
            0,
            0
        )
    ).toISOString();
const getAnalysisChartRange = (
    granularity: 'weekly' | 'monthly',
    amount: number
) => {
    if (granularity === 'monthly') {
        const currentMonthStart = startOfMonth(new Date());
        const start = subMonths(
            currentMonthStart,
            amount - 1
        );
        const end = endOfMonth(new Date());
        return {
            start: toUtcNoonISOString(start),
            end: toUtcNoonISOString(end),
        };
    }
    const currentWeekStart = startOfWeek(new Date(), {
        weekStartsOn: 1,
    });
    const start = subWeeks(
        currentWeekStart,
        amount - 1
    );
    const end = endOfWeek(new Date(), {
        weekStartsOn: 1,
    });
    return {
        start: toUtcNoonISOString(start),
        end: toUtcNoonISOString(end),
    };
};

// Metrics exposed by /conversions/kpis (time_series and time_series_compare).
// The Traffic metrics still come from the existing Traffic KPI hook.
const CONVERSIONS_METRICS = [
    { key: 'blended_conversions', title: 'Blended Conversions', format: 'float:2', color: '#0052b4', group: 'lead' },
    { key: 'paid_conversions', title: 'Paid Conversions', format: 'float:2', color: '#DAA335', group: 'lead' },
    { key: 'organic_conversions', title: 'Organic Conversions', format: 'float:2', color: '#18A055', group: 'lead' },
    { key: 'search_paid_conversions', title: 'Search Paid Conversions', format: 'float:2', color: '#DAA335', group: 'lead' },
    { key: 'shopping_paid_conversions', title: 'Shopping Paid Conversions', format: 'float:2', color: '#FF8C00', group: 'lead-shopping' },
    { key: 'pmax_paid_conversions', title: 'PMax Paid Conversions', format: 'float:2', color: '#FFE299', group: 'lead-pmax' },
    { key: 'blended_revenue', title: 'Blended Revenue', format: 'currency:2', color: '#18A055', group: 'ecommerce' },
    { key: 'paid_revenue', title: 'Paid Revenue', format: 'currency:2', color: '#DAA335', group: 'ecommerce' },
    { key: 'organic_revenue', title: 'Organic Revenue', format: 'currency:2', color: '#18A055', group: 'ecommerce' },
    { key: 'search_paid_revenue', title: 'Search Paid Revenue', format: 'currency:2', color: '#DAA335', group: 'ecommerce' },
    { key: 'shopping_paid_revenue', title: 'Shopping Paid Revenue', format: 'currency:2', color: '#FF8C00', group: 'ecommerce-shopping' },
    { key: 'pmax_paid_revenue', title: 'PMax Paid Revenue', format: 'currency:2', color: '#FFE299', group: 'ecommerce-pmax' },
    { key: 'blended_cvr', title: 'Blended CVR', format: 'percentage:2', color: '#5119B7', group: 'both' },
    { key: 'blended_cost_per_conv', title: 'Cost per Blended Conversion', format: 'currency:2', color: '#637381', group: 'both' },
    { key: 'paid_cost_per_conv', title: 'Cost per Paid Conversion', format: 'currency:2', color: '#919EAB', group: 'both' },
    { key: 'roas', title: 'ROAS', format: 'float:2', color: '#637381', group: 'ecommerce' },
    { key: 'blended_aov', title: 'Blended AOV', format: 'currency:2', color: '#919EAB', group: 'ecommerce' },
] as const;
const conversionKeys = new Set<string>(CONVERSIONS_METRICS.map((metric) => metric.key));
// The Conversions API uses SQL DATE strings; the chart store may hold ISO timestamps.
const toApiDate = (value?: string | null) => (value ? value.slice(0, 10) : '');

const MAX_METRICS = 3;
type GranularityOption = {
    value: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    label: string;
};
const GRANULARITY_OPTIONS: GranularityOption[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];
type PeriodOption = {
    value: string;
    label: string;
    amount: number;
};
const WEEKLY_PERIOD_OPTIONS: PeriodOption[] = [
    { value: 'last_week', label: 'Last week', amount: 1 },
    { value: 'last_2_weeks', label: 'Last 2 weeks', amount: 2 },
    { value: 'last_3_weeks', label: 'Last 3 weeks', amount: 3 },
    { value: 'last_4_weeks', label: 'Last 4 weeks', amount: 4 },
    { value: 'last_5_weeks', label: 'Last 5 weeks', amount: 5 },
    { value: 'last_6_weeks', label: 'Last 6 weeks', amount: 6 },
    { value: 'last_7_weeks', label: 'Last 7 weeks', amount: 7 },
    { value: 'last_8_weeks', label: 'Last 8 weeks', amount: 8 },
    { value: 'last_9_weeks', label: 'Last 9 weeks', amount: 9 },
    { value: 'last_10_weeks', label: 'Last 10 weeks', amount: 10 },
    { value: 'last_11_weeks', label: 'Last 11 weeks', amount: 11 },
    { value: 'last_12_weeks', label: 'Last 12 weeks', amount: 12 },
];
const MONTHLY_PERIOD_OPTIONS: PeriodOption[] = [
    { value: 'last_month', label: 'Last month', amount: 1 },
    { value: 'last_2_months', label: 'Last 2 months', amount: 2 },
    { value: 'last_3_months', label: 'Last 3 months', amount: 3 },
    { value: 'last_4_months', label: 'Last 4 months', amount: 4 },
    { value: 'last_5_months', label: 'Last 5 months', amount: 5 },
    { value: 'last_6_months', label: 'Last 6 months', amount: 6 },
    { value: 'last_7_months', label: 'Last 7 months', amount: 7 },
    { value: 'last_8_months', label: 'Last 8 months', amount: 8 },
    { value: 'last_9_months', label: 'Last 9 months', amount: 9 },
    { value: 'last_10_months', label: 'Last 10 months', amount: 10 },
    { value: 'last_11_months', label: 'Last 11 months', amount: 11 },
    { value: 'last_12_months', label: 'Last 12 months', amount: 12 },
];
export default function ConversionsAnalysisChartModal({
    open,
    onClose,
}: ConversionsAnalysisChartModalProps) {
    const filterStore = useSelector((state: RootState) => state.filters);
    const workspace: any = useCurrentWorkspace();
    const isLead = (workspace?.template ?? '').toLowerCase().includes('lead');
    const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);
    const apexChartInstanceRef = useRef<any>(null);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [granularity, setGranularity] =
        useState<GranularityOption>(() =>
            filterStore.timestep === 'year_month'
                ? GRANULARITY_OPTIONS[1]
                : GRANULARITY_OPTIONS[0]
        );
    const [period, setPeriod] = useState<PeriodOption>(
        WEEKLY_PERIOD_OPTIONS[1]
    );
    const dispatch = useDispatch();
    const periodOptions = useMemo(
        () =>
            granularity.value === 'monthly'
                ? MONTHLY_PERIOD_OPTIONS
                : WEEKLY_PERIOD_OPTIONS,
        [granularity.value]
    );
    useEffect(() => {
        if (!open) return;
        const defaultGranularity = GRANULARITY_OPTIONS[0];
        const defaultPeriod = WEEKLY_PERIOD_OPTIONS[1];
        const range = getAnalysisChartRange(
            'weekly',
            defaultPeriod.amount
        );
        setPeriod(defaultPeriod);
        setGranularity(defaultGranularity);
        dispatch(
            setAnalysisChartDate({
                start: range.start,
                end: range.end,
            })
        );
        dispatch(
            setAnalysisChartTimestep('year_week')
        );
    }, [open, dispatch]);
    const [previousPeriod, setPreviousPeriod] = useState(false);
    const [yearOverYear, setYearOverYear] = useState(false);
    const [showLabels, setShowLabels] = useState(false);
    const [matchScale, setMatchScale] = useState(false);
    /**
     * Liste des slots affichés dans la modal.
     *
     * Un slot vide correspond à un sélecteur "Select metric".
     * Au départ, seul "Metric 1" est affiché.
     */
    const [metricSlots, setMetricSlots] = useState<string[]>([
        isLead ? 'blended_conversions' : 'blended_revenue',
        'ad_cost',
        ''
    ]);
    /**
     * Liste des métriques sélectionnées mais temporairement masquées
     * via le bouton œil.
     *
     * La métrique reste présente dans son sélecteur, mais sa série
     * n'est plus transmise au graphique.
     */
    const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);
    useEffect(() => {
        setMetricSlots([isLead ? 'blended_conversions' : 'blended_revenue', 'ad_cost', '']);
        setHiddenMetrics([]);
    }, [workspace?.id, isLead]);
    /**
     * Liste de toutes les métriques actuellement sélectionnées,
     * y compris les métriques masquées.
     */
    const selectedMetrics = useMemo(
        () => metricSlots.filter(Boolean),
        [metricSlots]
    );
    /**
     * Liste des métriques réellement visibles dans le graphique.
     *
     * Une métrique masquée reste sélectionnée dans l'interface,
     * mais elle est retirée des séries rendues.
     */
    const visibleSelectedMetrics = useMemo(
        () =>
            selectedMetrics.filter(
                (metricKey) => !hiddenMetrics.includes(metricKey)
            ),
        [selectedMetrics, hiddenMetrics]
    );
    /**
     * Réutilisation du hook existant.
     *
     * Il fournit :
     * - le catalogue des KPI disponibles ;
     * - les données temporelles ;
     * - les formats ;
     * - les couleurs ;
     * - les restrictions métier selon le mode et le workspace.
     *
     * Seules les métriques non masquées sont transmises au graphique.
     */
    // Only pass Traffic keys into its existing hook. The Conversions keys
    // are loaded from /conversions/kpis instead of being queried as Traffic KPIs.
    const selectedTrafficMetrics = useMemo(
        () => selectedMetrics.filter((key) => !conversionKeys.has(key)),
        [selectedMetrics]
    );
    const trafficToggle = useKPIToggle(selectedTrafficMetrics);
    const analysisCompareEnabled =
        filterStore.analysisChartCompareMode !== 'none';
    const analysisStart = toApiDate(filterStore.analysisChartStart);
    const analysisEnd = toApiDate(filterStore.analysisChartEnd);
    const compareStart = analysisCompareEnabled
        ? toApiDate(filterStore.analysisChartCompareStart)
        : analysisStart;
    const compareEnd = analysisCompareEnabled
        ? toApiDate(filterStore.analysisChartCompareEnd)
        : analysisEnd;
    const {
        currentData: conversionsData,
        isFetching: isFetchingConversions,
        error: conversionsError,
    } = useGetConversionsKpisQuery(
        {
            workspaceId: workspace?.id,
            startDate: analysisStart,
            endDate: analysisEnd,
            compareStartDate: compareStart || analysisStart,
            compareEndDate: compareEnd || analysisEnd,
            countries: filterStore.countries ?? [],
            devices: filterStore.devices ?? [],
            platforms: filterStore.platforms ?? [],
            dimension: 'date',
            timestep: filterStore.analysisChartTimestep ?? 'year_week',
            // Backend analysis path: traffic_aggregated + existing conversions SQL.
            analysisChart: true,
        } as any,
        { skip: !open || !workspace?.id || !analysisStart || !analysisEnd }
    );
    const conversionMetrics = useMemo(() => {
        const rows = conversionsData?.time_series ?? [];
        return CONVERSIONS_METRICS.filter((metric) => {
            if (metric.group.startsWith('lead') && !isLead) return false;
            if (metric.group.startsWith('ecommerce') && isLead) return false;
            if (metric.group.endsWith('shopping') && !showShopping) return false;
            if (metric.group.endsWith('pmax') && !showPmax) return false;
            // Never display a conversion field absent from the actual API response.
            return rows.some((row: any) => Object.prototype.hasOwnProperty.call(row, metric.key));
        });
    }, [conversionsData?.time_series, isLead, showShopping, showPmax]);
    // Conversions define the bucket list because its SQL may omit a partial first
    // week. Match Traffic by its date when possible, then by index only when
    // both arrays have the same number of buckets.
    const toggle = useMemo(() => {
        const currentTrafficRows = trafficToggle.statsData?.time_series_data ?? [];
        const currentConversionRows = conversionsData?.time_series ?? [];
        const comparisonTrafficRows = trafficToggle.compareStatsData?.time_series_data ?? [];
        const comparisonConversionRows = conversionsData?.time_series_compare ?? [];
        const bucketKey = (row: any): string => {
            const raw = row?.date_day ?? row?.date ?? row?.year_week ??
                row?.year_month ?? row?.dimension;
            return raw == null ? '' : String(raw).slice(0, 10);
        };
        const combine = (trafficRows: any[], conversionRows: any[]) => {
            if (!conversionRows.length) return trafficRows;
            const trafficByDate = new Map(
                trafficRows.filter((row: any) => bucketKey(row)).map((row: any) => [bucketKey(row), row])
            );
            const hasMatchingDates = conversionRows.some((row: any) =>
                trafficByDate.has(bucketKey(row))
            );
            return conversionRows.map((row: any, index: number) => {
                const match = hasMatchingDates
                    ? trafficByDate.get(bucketKey(row))
                    : trafficRows.length === conversionRows.length
                        ? trafficRows[index]
                        : undefined;
                // Prefer real Traffic date metadata if it exists; otherwise use
                // the Conversions API dimension (formatted week/month/date).
                return {
                    ...match,
                    ...row,
                    date: match?.date ?? row.dimension,
                    year_week: match?.year_week ?? row.dimension,
                    year_month: match?.year_month ?? row.dimension,
                };
            });
        };
        const conversionSummary = {
            blended_conversions: conversionsData?.summary?.blended,
            paid_conversions: conversionsData?.summary?.paid,
            organic_conversions: conversionsData?.summary?.organic,
            blended_revenue: conversionsData?.summary?.blended,
            paid_revenue: conversionsData?.summary?.paid,
            organic_revenue: conversionsData?.summary?.organic,
            blended_cvr: conversionsData?.summary?.blended_cvr,
            blended_cost_per_conv: conversionsData?.summary?.cost_per_blended_conv,
        };
        return {
            ...trafficToggle,
            stats: [...trafficToggle.stats, ...conversionMetrics],
            statsData: {
                ...trafficToggle.statsData,
                ...conversionSummary,
                ...Object.fromEntries(
                    conversionMetrics.map((metric) => [
                        metric.key,
                        currentConversionRows[0]?.[metric.key],
                    ])
                ),
                time_series_data: combine(currentTrafficRows, currentConversionRows),
            },
            compareStatsData: {
                ...trafficToggle.compareStatsData,
                time_series_data: combine(comparisonTrafficRows, comparisonConversionRows),
            },
            kpiToggle: selectedMetrics,
            isLoadingStats: trafficToggle.isLoadingStats || isFetchingConversions,
            isLoadingCompareStats:
                trafficToggle.isLoadingCompareStats ||
                (analysisCompareEnabled && isFetchingConversions),
            statsError: trafficToggle.statsError ?? conversionsError,
        };
    }, [
        trafficToggle, conversionsData, conversionMetrics, selectedMetrics,
        isFetchingConversions, conversionsError, analysisCompareEnabled,
    ]);
    /**
     * Liste des métriques disponibles dans les sélecteurs.
     *
     * On retire uniquement les KPI explicitement indisponibles,
     * identifiés par une valeur égale à -1.
     *
     * Une valeur undefined est conservée, car les données peuvent
     * encore être en cours de chargement.
     */
    const availableMetrics = useMemo(
        () =>
            toggle.stats.filter((stat: any) => {
                const value = toggle.statsData?.[stat.key];
                // Même règle que la liste des métriques des cards Traffic :
                // la métrique doit exister dans statsData et ne pas être indisponible (-1).
                return value !== undefined && value !== -1;
            }),
        [toggle.stats, toggle.statsData]
    );
    /**
     * Retourne le libellé traduit d'une métrique à partir de sa clé.
     */
    const getMetricLabel = (metricKey: string) =>
        availableMetrics.find((metric: any) => metric.key === metricKey)?.title ??
        metricKey;
    /**
     * Remplace, ajoute ou retire une métrique dans un slot.
     *
     * Comportement :
     * - lorsqu'une métrique est sélectionnée dans le dernier slot,
     *   un nouveau slot vide est créé automatiquement ;
     * - maximum 3 slots ;
     * - lorsqu'une métrique est retirée, les slots sont compactés ;
     * - lorsqu'une métrique masquée est remplacée ou supprimée,
     *   elle est retirée de hiddenMetrics.
     */
    const handleMetricChange = (index: number, newMetricKey: string) => {
        const previousMetricKey = metricSlots[index];
        // La dernière métrique sélectionnée ne peut pas être supprimée.
        if (!newMetricKey && selectedMetrics.length <= 1) {
            return;
        }
        /**
         * Si l'ancienne métrique était masquée et qu'elle est remplacée
         * ou supprimée, on nettoie son état de visibilité.
         */
        if (previousMetricKey && previousMetricKey !== newMetricKey) {
            setHiddenMetrics((currentHiddenMetrics) =>
                currentHiddenMetrics.filter(
                    (metricKey) => metricKey !== previousMetricKey
                )
            );
        }
        setMetricSlots((currentSlots) => {
            const nextSlots = [...currentSlots];
            nextSlots[index] = newMetricKey;
            /**
             * Suppression d'une métrique.
             *
             * On compacte les métriques restantes et on conserve
             * un seul slot vide à la fin tant que la limite n'est pas atteinte.
             */
            if (!newMetricKey) {
                const remainingMetrics = nextSlots.filter(Boolean);
                if (remainingMetrics.length >= MAX_METRICS) {
                    return remainingMetrics.slice(0, MAX_METRICS);
                }
                return [...remainingMetrics, ''];
            }
            /**
             * Si une métrique vient d'être sélectionnée dans le dernier slot,
             * on ajoute automatiquement le slot suivant.
             */
            const isLastSlot = index === nextSlots.length - 1;
            const canAddAnotherSlot = nextSlots.length < MAX_METRICS;
            if (isLastSlot && canAddAnotherSlot) {
                nextSlots.push('');
            }
            return nextSlots;
        });
    };
    /**
     * Masque ou réaffiche une métrique dans le graphique.
     *
     * La sélection du champ n'est pas modifiée.
     */
    const handleToggleMetricVisibility = (metricKey: string) => {
        if (!metricKey) return;
        const isCurrentlyHidden = hiddenMetrics.includes(metricKey);
        // Une métrique masquée peut toujours être réaffichée.
        if (isCurrentlyHidden) {
            setHiddenMetrics((current) =>
                current.filter((key) => key !== metricKey)
            );
            return;
        }
        // Impossible de masquer la dernière série visible.
        if (visibleSelectedMetrics.length <= 1) {
            return;
        }
        setHiddenMetrics((current) => [...current, metricKey]);
    };
    /**
     * Génère un sélecteur de métrique.
     *
     * Les métriques déjà sélectionnées dans les autres slots
     * sont retirées de la liste, afin d'empêcher les doublons.
     */
    const renderMetricSelect = (
        value: string,
        index: number
    ) => {
        const label = `Metric ${index + 1}`;
        const isHidden =
            Boolean(value) &&
            hiddenMetrics.includes(value);
        const cannotHideMetric =
            Boolean(value) &&
            !isHidden &&
            visibleSelectedMetrics.length <= 1;
        // Ticket #33 :
        // on réutilise exactement la même liste, les mêmes libellés et le même ordre
        // que les métriques disponibles dans les cards Traffic.
        // `availableMetrics` conserve l'ordre de `toggle.stats` et les `stat.title`
        // viennent du même catalogue KPI.
        const selectableMetrics = availableMetrics;
        return (
            <FormControl
                key={`metric-slot-${index}`}
                fullWidth
                sx={{
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        pt: '8px',
                    }}
                >
                    {/* Label placé sur la bordure du champ */}
                    <Typography
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: '12px',
                            zIndex: 1,
                            px: '4px',
                            backgroundColor: '#FFFFFF',
                            color: '#91A0AF',
                            fontFamily: 'Public Sans',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '12px',
                        }}
                    >
                        {label}
                    </Typography>
                    <Autocomplete
                        fullWidth
                        options={selectableMetrics}
                        value={
                            selectableMetrics.find(
                                (metric: any) => metric?.key === value
                            ) ?? null
                        }
                        onChange={(_, newValue) => {
                            handleMetricChange(
                                index,
                                newValue?.key ?? ''
                            );
                        }}
                        getOptionLabel={(option: any) => option?.title ?? ''}
                        isOptionEqualToValue={(option: any, currentValue: any) =>
                            Boolean(option?.key && currentValue?.key) &&
                            option.key === currentValue.key
                        }
                        popupIcon={
                            <Iconify
                                icon="eva:chevron-down-fill"
                                width={20}
                                sx={{ color: '#91A0AF' }}
                            />
                        }
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: '6px',
                                    minHeight: '260px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow:
                                        '0px 4px 12px rgba(145, 158, 171, 0.24)',
                                },
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '46px',
                                minHeight: '46px',
                                pr: '70px !important',
                            },
                            '& .MuiAutocomplete-input': {
                                fontFamily: 'Public Sans',
                                fontSize: '16px',
                                fontWeight: 400,
                                color: isHidden ? '#919EAB' : '#333C47',
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                right: '12px',
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select metric"
                                inputProps={{
                                    ...params.inputProps,
                                }}
                            />
                        )}
                        disableClearable
                        renderOption={(props, option: any) => (
                            <Box
                                component="li"
                                {...props}
                                key={option.key}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Public Sans',
                                        fontSize: '14px',
                                        color: '#333C47',
                                    }}
                                >
                                    {option.title}
                                </Typography>
                            </Box>
                        )}
                    />
                    <IconButton
                        size="small"
                        disabled={!value || cannotHideMetric}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleToggleMetricVisibility(value);
                        }}
                        sx={{
                            position: 'absolute',
                            right: '38px',
                            top: 'calc(50% + 4px)',
                            transform: 'translateY(-50%)',
                            zIndex: 3,
                            width: 32,
                            height: 32,
                            p: 0,
                            color: isHidden ? '#C4CDD5' : '#91A0AF',
                            '&:hover': {
                                backgroundColor: 'rgba(145, 158, 171, 0.08)',
                            },
                            '&.Mui-disabled': {
                                color: '#C4CDD5',
                            },
                        }}
                    >
                        <Iconify
                            icon={
                                isHidden
                                    ? 'solar:eye-closed-linear'
                                    : 'solar:eye-linear'
                            }
                            width={22}
                        />
                    </IconButton>
                </Box>
            </FormControl>
        );
    };
    /**
 * Active ou désactive la comparaison avec la période précédente.
 *
 * Pour une plage principale de 2 semaines :
 * - période principale : semaines N-1 et N ;
 * - comparaison : semaines N-3 et N-2.
 */
    const handlePreviousPeriodChange = (checked: boolean) => {
        setPreviousPeriod(checked);
        // Les deux comparaisons sont exclusives.
        if (checked) {
            setYearOverYear(false);
        }
        if (!checked) {
            dispatch(setAnalysisChartCompareMode('none'));
            return;
        }
        const currentStart = new Date(
            filterStore.analysisChartStart
        );
        const currentEnd = new Date(
            filterStore.analysisChartEnd
        );
        const durationInMilliseconds =
            currentEnd.getTime() - currentStart.getTime();
        const compareEnd = subDays(currentStart, 1);
        const compareStart = new Date(
            compareEnd.getTime() -
            durationInMilliseconds
        );
        dispatch(
            setAnalysisChartCompareDate({
                compareStart:
                    toUtcNoonISOString(compareStart),
                compareEnd:
                    toUtcNoonISOString(compareEnd),
            })
        );
        dispatch(
            setAnalysisChartCompareMode(
                'previous_period'
            )
        );
    };
    /**
     * Active ou désactive la comparaison année sur année.
     *
     * La période comparée conserve les mêmes bornes,
     * décalées d'un an.
     */
    const handleYearOverYearChange = (
        checked: boolean
    ) => {
        setYearOverYear(checked);
        // Les deux comparaisons sont exclusives.
        if (checked) {
            setPreviousPeriod(false);
        }
        if (!checked) {
            dispatch(setAnalysisChartCompareMode('none'));
            return;
        }
        const currentStart = new Date(
            filterStore.analysisChartStart
        );
        const currentEnd = new Date(
            filterStore.analysisChartEnd
        );
        dispatch(
            setAnalysisChartCompareDate({
                compareStart:
                    toUtcNoonISOString(
                        subYears(currentStart, 1)
                    ),
                compareEnd:
                    toUtcNoonISOString(
                        subYears(currentEnd, 1)
                    ),
            })
        );
        dispatch(
            setAnalysisChartCompareMode(
                'year_over_year'
            )
        );
    };
    /**
     * Ferme la modal.
     *
     * Les sélections restent actuellement conservées lors d'une réouverture,
     * car le composant reste monté dans ExploreTable.
     */
    const handleClose = () => {
        onClose();
    };
    const loadExportImage = (
        source: string
    ): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () =>
                reject(
                    new Error(
                        'Unable to load ApexCharts PNG'
                    )
                );
            image.src = source;
        });
    const exportComparisonValues = useMemo(() => {
        if (filterStore.analysisChartCompareMode === 'none') {
            return [];
        }
        const currentTimeSeries =
            toggle.statsData?.time_series_data ?? [];
        const compareTimeSeries =
            toggle.compareStatsData?.time_series_data ?? [];
        if (
            currentTimeSeries.length === 0 ||
            compareTimeSeries.length === 0
        ) {
            return [];
        }
        return currentTimeSeries.map(
            (currentBucket: any, bucketIndex: number) => {
                const compareBucket =
                    compareTimeSeries[bucketIndex];
                return visibleSelectedMetrics.map(
                    (metricKey) => {
                        const stat = toggle.stats.find(
                            (item: any) =>
                                item.key === metricKey
                        );
                        const currentValue =
                            Number(
                                currentBucket?.[metricKey]
                            ) || 0;
                        const compareValue =
                            Number(
                                compareBucket?.[metricKey]
                            ) || 0;
                        let value: number | null = null;
                        if (compareValue !== 0) {
                            value =
                                ((currentValue -
                                    compareValue) /
                                    Math.abs(compareValue)) *
                                100;
                        } else if (currentValue === 0) {
                            value = 0;
                        }
                        return {
                            metricKey,
                            color: getMetricEvolutionColor(
                                metricKey,
                                stat?.title ?? '',
                                stat?.color ?? '#919EAB'
                            ),
                            value,
                        };
                    }
                );
            }
        );
    }, [
        filterStore.analysisChartCompareMode,
        toggle.statsData,
        toggle.compareStatsData,
        toggle.stats,
        visibleSelectedMetrics,
    ]);
    const handleExportChart = async (
        format: ExportFormat,
        options: ExportOptions
    ) => {
        const chartInstance =
            apexChartInstanceRef.current;
        if (
            !chartInstance ||
            typeof chartInstance.dataURI !== 'function'
        ) {
            console.error(
                'ApexCharts instance unavailable',
                chartInstance
            );
            return;
        }
        const selectedFormat =
            EXPORT_FORMATS.find(
                (item) => item.value === format
            );
        if (!selectedFormat) {
            console.error(
                'Unknown export format:',
                format
            );
            return;
        }
        /*
         * On sauvegarde les options actuelles pour pouvoir
         * restaurer le graphique après l’export.
         */
        const currentConfig =
            chartInstance.w?.config;
        const originalChartBackground =
            currentConfig?.chart?.background;
        const originalXaxis =
            currentConfig?.xaxis;
        const originalYaxis =
            currentConfig?.yaxis;
        try {
            await document.fonts.ready;
            /*
             * Options temporaires utilisées uniquement
             * pendant la génération de l’image.
             */
            const temporaryOptions: any = {
                chart: {
                    background: options.transparent
                        ? 'rgba(0, 0, 0, 0)'
                        : '#FFFFFF',
                },
            };
            if (options.hideAxes) {
                temporaryOptions.xaxis = {
                    ...originalXaxis,
                    labels: {
                        ...originalXaxis?.labels,
                        show: false,
                    },
                    axisBorder: {
                        ...originalXaxis?.axisBorder,
                        show: false,
                    },
                    axisTicks: {
                        ...originalXaxis?.axisTicks,
                        show: false,
                    },
                    title: {
                        ...originalXaxis?.title,
                        text: undefined,
                    },
                };
                const yaxisList =
                    Array.isArray(originalYaxis)
                        ? originalYaxis
                        : [originalYaxis ?? {}];
                temporaryOptions.yaxis =
                    yaxisList.map((axis: any) => ({
                        ...axis,
                        show: false,
                        labels: {
                            ...axis?.labels,
                            show: false,
                        },
                        axisBorder: {
                            ...axis?.axisBorder,
                            show: false,
                        },
                        axisTicks: {
                            ...axis?.axisTicks,
                            show: false,
                        },
                        title: {
                            ...axis?.title,
                            text: undefined,
                        },
                    }));
            }
            /*
             * Applique temporairement le fond et/ou
             * la suppression des axes.
             */
            await chartInstance.updateOptions(
                temporaryOptions,
                false,
                false,
                false
            );
            if (options.transparent) {
                const chartEl = chartInstance.el;
                chartEl
                    ?.querySelectorAll(
                        '.apexcharts-canvas, .apexcharts-svg'
                    )
                    .forEach((element: HTMLElement) => {
                        element.style.background = 'transparent';
                    });
            }
            /*
             * Export natif ApexCharts.
             */
            const result =
                await chartInstance.dataURI({
                    scale: 2,
                });
            if (!result?.imgURI) {
                throw new Error(
                    'ApexCharts did not return an imgURI'
                );
            }
            const sourceImage =
                await loadExportImage(
                    result.imgURI
                );
            /*
             * Création du canvas final selon le format :
             * carré, paysage, portrait ou pleine largeur.
             */
            const canvas =
                document.createElement('canvas');
            const exportScale = 2;
            canvas.width =
                selectedFormat.width * exportScale;
            canvas.height =
                selectedFormat.height * exportScale;
            const context =
                canvas.getContext('2d');
            const legendItems = visibleSelectedMetrics
                .map((metricKey) => {
                    const stat = toggle.stats.find(
                        (item: any) => item.key === metricKey
                    );
                    return stat
                        ? {
                            label: stat.title,
                            color: getMetricEvolutionColor(
                                metricKey,
                                stat.title,
                                stat.color
                            ),
                        }
                        : null;
                })
                .filter(Boolean) as {
                    label: string;
                    color: string;
                }[];
            if (!context) {
                throw new Error(
                    'Unable to create export canvas'
                );
            }
            /*
             * Pour un export transparent, on ne remplit
             * volontairement pas le canvas.
             */
            if (!options.transparent) {
                context.fillStyle = '#FFFFFF';
                context.fillRect(
                    0,
                    0,
                    canvas.width,
                    canvas.height
                );
            }
            const padding = 48;
            const availableWidth =
                canvas.width - padding * 2;
            const availableHeight =
                canvas.height - padding * 2;
            /*
             * Le graphique garde son ratio original.
             */
            const imageScale = Math.min(
                availableWidth /
                sourceImage.width,
                availableHeight /
                sourceImage.height
            );
            const renderedWidth =
                sourceImage.width *
                imageScale;
            const renderedHeight =
                sourceImage.height *
                imageScale;
            const positionX =
                (canvas.width -
                    renderedWidth) /
                2;
            const positionY =
                (canvas.height -
                    renderedHeight) /
                2;
            context.font = `${13 * exportScale}px Public Sans`;
            context.textBaseline = 'middle';
            let legendX = canvas.width - 48;
            [...legendItems].reverse().forEach((item) => {
                const textWidth =
                    context.measureText(item.label).width;
                legendX -= textWidth;
                context.fillStyle = '#637381';
                context.fillText(
                    item.label,
                    legendX,
                    160
                );
                legendX -= 12;
                context.beginPath();
                context.fillStyle = item.color;
                context.arc(
                    legendX,
                    160,
                    3,
                    0,
                    Math.PI * 2
                );
                context.fill();
                legendX -= 20;
            });
            context.drawImage(
                sourceImage,
                positionX,
                positionY,
                renderedWidth,
                renderedHeight
            );
            if (exportComparisonValues.length > 0) {
                const chartElement: any = chartInstance.el;
                const chartRect =
                    chartElement.getBoundingClientRect();
                const xAxisLabels =
                    chartElement.querySelectorAll<SVGTextElement>(
                        '.apexcharts-xaxis-texts-g .apexcharts-xaxis-label'
                    );
                const xPositions = Array.from(
                    xAxisLabels
                ).map((label: any) => {
                    const labelRect =
                        label.getBoundingClientRect();
                    const relativeCenterX =
                        (
                            labelRect.left +
                            labelRect.width / 2 -
                            chartRect.left
                        ) / chartRect.width;
                    return (
                        positionX +
                        relativeCenterX * renderedWidth
                    );
                });
                const comparisonTop =
                    positionY +
                    renderedHeight +
                    24 * exportScale;
                context.font =
                    `${12 * exportScale}px Public Sans`;
                context.textBaseline = 'middle';
                exportComparisonValues.forEach(
                    (bucketValues: any, bucketIndex: any) => {
                        const x = xPositions[bucketIndex];
                        if (x === undefined) {
                            return;
                        }
                        bucketValues.forEach(
                            (metric: any, metricIndex: any) => {
                                const value = metric.value;
                                const textColor =
                                    value === null ||
                                        value === 0
                                        ? '#919EAB'
                                        : value > 0
                                            ? '#22A06B'
                                            : '#FF5630';
                                const text =
                                    value === null
                                        ? '—'
                                        : value > 999
                                            ? '>999%'
                                            : value < -999
                                                ? '<-999%'
                                                : `${value > 0 ? '+' : ''}${Math.round(value)}%`;
                                const y =
                                    comparisonTop +
                                    metricIndex *
                                    18 *
                                    exportScale;
                                /*
                                 * Premier bucket :
                                 * pastille KPI + valeur
                                 */
                                if (bucketIndex === 0) {
                                    const dotRadius =
                                        4 * exportScale;
                                    const gap =
                                        6 * exportScale;
                                    const textWidth =
                                        context.measureText(
                                            text
                                        ).width;
                                    const totalWidth =
                                        dotRadius * 2 +
                                        gap +
                                        textWidth;
                                    const startX =
                                        x - totalWidth / 2;
                                    context.beginPath();
                                    context.fillStyle =
                                        metric.color;
                                    context.arc(
                                        startX + dotRadius,
                                        y,
                                        dotRadius,
                                        0,
                                        Math.PI * 2
                                    );
                                    context.fill();
                                    context.fillStyle =
                                        textColor;
                                    context.textAlign =
                                        'left';
                                    context.fillText(
                                        text,
                                        startX +
                                        dotRadius * 2 +
                                        gap,
                                        y
                                    );
                                } else {
                                    context.fillStyle =
                                        textColor;
                                    context.textAlign =
                                        'center';
                                    context.fillText(
                                        text,
                                        x,
                                        y
                                    );
                                }
                            }
                        );
                    }
                );
                context.textAlign = 'start';
            }
            const pngUrl =
                canvas.toDataURL(
                    'image/png'
                );
            const variantName = [
                options.transparent
                    ? 'transparent'
                    : 'background',
                options.hideAxes
                    ? 'no-axes'
                    : 'with-axes',
            ].join('-');
            const link =
                document.createElement('a');
            link.href = pngUrl;
            link.download =
                `metric-evolution-${format}-${variantName}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportModalOpen(false);
        } catch (error) {
            console.error(
                `Unable to export ${format} PNG:`,
                error
            );
        } finally {
            /*
             * Restaure toujours le graphique tel qu’il était,
             * même si l’export a échoué.
             */
            const restoreOptions: any = {
                chart: {
                    background:
                        originalChartBackground,
                },
            };
            if (options.hideAxes) {
                restoreOptions.xaxis =
                    originalXaxis;
                restoreOptions.yaxis =
                    originalYaxis;
            }
            try {
                await chartInstance.updateOptions(
                    restoreOptions,
                    false,
                    false,
                    false
                );
            } catch (restoreError) {
                console.error(
                    'Unable to restore chart options:',
                    restoreError
                );
            }
        }
    };
    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: '1600px',
                        maxWidth: 'calc(100vw - 48px)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#FFFFFF',
                        boxShadow:
                            '0px 24px 48px rgba(145, 158, 171, 0.24)',
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(33, 43, 54, 0.48)',
                    },
                }}
            >
                {/* Header de la modal */}
                <Box
                    sx={{
                        height: '60px',
                        minHeight: '60px',
                        px: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#FAFBFB',
                        borderBottom: '1px solid rgba(145, 158, 171, 0.20)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#333C47',
                                fontFamily: 'Public Sans',
                                fontSize: '18px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                mr: "16px",
                                lineHeight: '100%',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Metric Evolution
                        </Typography>
                        <Autocomplete
                            disableClearable
                            options={GRANULARITY_OPTIONS}
                            value={granularity}
                            onChange={(_, newValue) => {
                                if (!newValue) return;
                                setGranularity(newValue);
                                const isMonthly =
                                    newValue.value === 'monthly';
                                const nextPeriod = isMonthly
                                    ? MONTHLY_PERIOD_OPTIONS[1]
                                    : WEEKLY_PERIOD_OPTIONS[1];
                                setPeriod(nextPeriod);
                                const range = getAnalysisChartRange(
                                    isMonthly ? 'monthly' : 'weekly',
                                    nextPeriod.amount
                                );
                                dispatch(
                                    setAnalysisChartTimestep(
                                        isMonthly
                                            ? 'year_month'
                                            : 'year_week'
                                    )
                                );
                                dispatch(
                                    setAnalysisChartDate({
                                        start: range.start,
                                        end: range.end,
                                    })
                                );
                            }}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, currentValue) =>
                                option.value === currentValue.value
                            }
                            popupIcon={
                                <Iconify
                                    icon="eva:chevron-down-fill"
                                    width={18}
                                    sx={{ color: '#91A0AF' }}
                                />
                            }
                            sx={{
                                width: 120,
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    minHeight: 40,
                                    p: '8px 8px',
                                    borderRadius: '12px',
                                },
                                '& .MuiAutocomplete-input': {
                                    width: 0,
                                    minWidth: 0,
                                    opacity: 0,
                                    p: '0 !important',
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    right: 8,
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        readOnly: true,
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '28px',
                                                    px: '12px',
                                                    borderRadius: '8px',
                                                    bgcolor: '#F4F6F8',
                                                    color: '#333C47',
                                                    fontFamily: 'Public Sans',
                                                    fontSize: '13px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    lineHeight: '23px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {granularity.label}
                                            </Box>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Autocomplete
                            disableClearable
                            options={periodOptions}
                            value={period}
                            onChange={(_, newValue) => {
                                if (!newValue) return;
                                setPeriod(newValue);
                                const range = getAnalysisChartRange(
                                    granularity.value === 'monthly'
                                        ? 'monthly'
                                        : 'weekly',
                                    newValue.amount
                                );
                                dispatch(
                                    setAnalysisChartDate({
                                        start: range.start,
                                        end: range.end,
                                    })
                                );
                            }}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, currentValue) =>
                                option.value === currentValue.value
                            }
                            popupIcon={
                                <Iconify
                                    icon="eva:chevron-down-fill"
                                    width={18}
                                    sx={{ color: '#91A0AF' }}
                                />
                            }
                            sx={{
                                width: 150,
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    minHeight: 40,
                                    p: '4px 8px',
                                    borderRadius: '12px',
                                },
                                '& .MuiAutocomplete-input': {
                                    width: 0,
                                    minWidth: 0,
                                    opacity: 0,
                                    p: '0 !important',
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    right: 8,
                                },
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        mt: '4px',
                                        borderRadius: '8px',
                                        boxShadow: '0px 4px 12px rgba(145, 158, 171, 0.24)',
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight: '260px',
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        value: '',
                                        readOnly: true,
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '28px',
                                                    px: '12px',
                                                    borderRadius: '8px',
                                                    bgcolor: '#F4F6F8',
                                                    color: '#333C47',
                                                    fontFamily: 'Public Sans',
                                                    fontSize: '13px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    lineHeight: '23px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {period.label}
                                            </Box>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label="Close metric evolution modal"
                        sx={{
                            color: '#637381',
                        }}
                    >
                        <Iconify icon="mingcute:close-line" width={20} />
                    </IconButton>
                </Box>
                <DialogContent
                    sx={{
                        p: '24px 20px 32px',
                    }}
                >
                    {/* Sélecteurs de métriques */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: `repeat(${MAX_METRICS}, minmax(0, 1fr))`,
                            },
                            gap: '24px',
                        }}
                    >
                        {metricSlots.map((metric, index) =>
                            renderMetricSelect(metric, index)
                        )}
                    </Box>
                    {/* Le graphique apparaît dès qu'au moins une métrique est sélectionnée */}
                    {selectedMetrics.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <ExploreChart
                                toggle={toggle}
                                filterStore={filterStore}
                                hiddenMetrics={hiddenMetrics}
                                showLabels={showLabels}
                                matchScale={matchScale}
                                onChartMounted={(chartInstance) => {
                                    apexChartInstanceRef.current = chartInstance;
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <Box
                    sx={{
                        height: '54px',
                        px: '20px',
                        py: '12px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTop: '1px solid rgba(145,158,171,0.20)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: "12px",
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={previousPeriod}
                                    onChange={(event) =>
                                        handlePreviousPeriodChange(
                                            event.target.checked
                                        )
                                    }
                                    sx={{
                                        color: '#C4CDD5',
                                        '&.Mui-checked': {
                                            color: '#118D57',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        '&.Mui-checked:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                />
                            }
                            label="Previous Period"
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#637381',
                                    textAlign: 'center',
                                    fontFamily: 'Public Sans',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    lineHeight: '100%', // 14px
                                },
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={yearOverYear}
                                    onChange={(event) =>
                                        handleYearOverYearChange(
                                            event.target.checked
                                        )
                                    }
                                    sx={{
                                        color: '#C4CDD5',
                                        '&.Mui-checked': {
                                            color: '#118D57',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        '&.Mui-checked:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                />
                            }
                            label="Year over Year"
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#637381',
                                    textAlign: 'center',
                                    fontFamily: 'Public Sans',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    lineHeight: '100%', // 14px
                                },
                            }}
                        />
                        <svg xmlns="http\\://www\.w3.org/2000/svg" width="1" height="21" viewBox="0 0 1 21" fill="none">
                            <path d="M0.5 0.5V20.5" stroke="#919EAB" stroke-opacity="0.4" stroke-linecap="round" />
                        </svg>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showLabels}
                                    onChange={(event) =>
                                        setShowLabels(event.target.checked)
                                    }
                                    sx={{
                                        color: '#C4CDD5',
                                        '&.Mui-checked': {
                                            color: '#118D57',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        '&.Mui-checked:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                />
                            }
                            label="Show labels"
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#637381',
                                    textAlign: 'center',
                                    fontFamily: 'Public Sans',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    lineHeight: '100%', // 14px
                                },
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={matchScale}
                                    onChange={(event) =>
                                        setMatchScale(event.target.checked)
                                    }
                                    sx={{
                                        color: '#C4CDD5',
                                        '&.Mui-checked': {
                                            color: '#118D57',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        '&.Mui-checked:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                />
                            }
                            label="Match scales"
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#637381',
                                    textAlign: 'center',
                                    fontFamily: 'Public Sans',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    lineHeight: '100%', // 14px
                                },
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => setExportModalOpen(true)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            px: '12px',
                            py: '6px',
                            minWidth: 'auto',
                            height: 'auto',
                            borderRadius: '8px',
                            backgroundColor: '#181818',
                            textTransform: 'none',
                            fontFamily: 'Public Sans',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '18px',
                            '&:hover': {
                                backgroundColor: '#181818',
                            },
                        }}
                    >
                        Export chart
                    </Button>
                </Box>
            </Dialog>
            <ExploreChartExportModal
                open={exportModalOpen}
                onClose={() =>
                    setExportModalOpen(false)
                }
                onExport={(format, options) => {
                    void handleExportChart(
                        format,
                        options
                    );
                }}
            />
        </>
    );
}